import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Paciente } from '../types';
import { fetchPacientes, createPaciente, updatePaciente } from '../services/pacientesService';

export const usePacientes = () => {
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPacientes = async (params?: any) => {
    setLoading(true);
    try {
      const data = await fetchPacientes(params);

      let result: Paciente[] = [];
      // Verificar si los datos tienen la estructura esperada con paginación
      if (data && typeof data === 'object' && 'pacientes' in data && Array.isArray(data.pacientes)) {
        result = data.pacientes;
      } else if (Array.isArray(data)) {
        result = data;
      } else {
        console.error('Formato de datos incorrecto:', data);
        throw new Error('Formato de datos incorrecto. No se encontró un array de pacientes válido.');
      }

      setPacientes(result);
      setError(null);
      return result;
    } catch (err: any) {
      console.error('Error al cargar pacientes:', err);
      setError(err.message || 'Error al cargar pacientes');
      toast.error('Error al cargar pacientes');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const addPaciente = async (pacienteData: Omit<Paciente, 'codigo'>) => {
    setLoading(true);
    try {
      const newPaciente = await createPaciente(pacienteData);
      // Cargar todos los pacientes nuevamente en lugar de solo añadir el nuevo
      await loadPacientes();
      setError(null);
      return newPaciente;
    } catch (err: any) {
      const errorMessage = err.message || 'Error al crear paciente';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const editPaciente = async (codigo: string, pacienteData: Partial<Paciente>) => {
    setLoading(true);
    try {
      // Send the clean payload: no stale merge of the local list row.
      const updatedPaciente = await updatePaciente(codigo, pacienteData);
      // Update the list in place so the active search and pagination stay in
      // sync. If the patient is not in the local list the server update still
      // succeeded and we return the updated patient.
      setPacientes((prev) =>
        prev.map((p) => (p.codigo === codigo ? { ...p, ...updatedPaciente } : p))
      );
      setError(null);
      return updatedPaciente;
    } catch (err: any) {
      const errorMessage = err.message || 'Error al actualizar paciente';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Cargar todos los pacientes sin paginación para reportes
    loadPacientes({ limit: 10000 });
  }, []);

  return {
    pacientes,
    loading,
    error,
    addPaciente,
    editPaciente,
    loadPacientes,
  };
};